import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Trash2, Inbox, UserPlus, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUSES = [
  { key: "new", label: "New", cls: "bg-teal text-white" },
  { key: "contacted", label: "Contacted", cls: "bg-amber-100 text-amber-800" },
  { key: "enrolled", label: "Enrolled", cls: "bg-green-100 text-green-800" },
  { key: "archived", label: "Archived", cls: "bg-gray-200 text-gray-600" },
];
const FILTERS = [{ key: "", label: "All" }, ...STATUSES];

export default function JoinRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/join-requests", { params: filter ? { status: filter } : {} })
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Could not load inquiries"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const setStatus = async (id, status) => {
    await api.post(`/join-requests/${id}/status`, { status });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry? This can't be undone.")) return;
    await api.delete(`/join-requests/${id}`);
    toast.success("Inquiry deleted");
    load();
  };
  const convert = async (r) => {
    if (r.converted_parent_id) return;
    try {
      const { data } = await api.post(`/join-requests/${r.id}/convert`);
      toast.success(data.dancer_name || r.dancer_name
        ? "Created parent + dancer in People 🎉"
        : "Created parent in People 🎉");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not convert"); }
  };

  return (
    <div className="space-y-6" data-testid="join-requests-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight flex items-center gap-2">
          <Inbox className="w-7 h-7 text-teal" />Hub Access Requests
        </h1>
        <p className="text-muted-foreground mt-1">Already-enrolled families who requested access to the Studio V Hub. Review each against your People records before granting access — this form never creates accounts.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((s) => (
          <button key={s.key} data-testid={`join-filter-${s.key || "all"}`} onClick={() => setFilter(s.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${filter === s.key ? "bg-black text-white border-black" : "bg-white hover:border-gray-300"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-12 text-center text-muted-foreground" data-testid="join-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No inquiries here yet. New requests from the sign-in page will appear automatically.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((r) => {
            const st = STATUSES.find((s) => s.key === r.status) || STATUSES[0];
            return (
              <div key={r.id} data-testid={`join-card-${r.id}`} className="rounded-2xl border bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-head font-bold text-lg truncate">{r.parent_name}</p>
                    <a href={`mailto:${r.email}`} className="text-sm text-teal-dark hover:underline flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{r.email}</a>
                    {r.phone && <a href={`tel:${r.phone}`} className="text-sm text-muted-foreground hover:underline flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{r.phone}</a>}
                  </div>
                  <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {r.dancer_name && <span className="rounded-lg bg-gray-50 border px-2 py-1">Dancer: <b>{r.dancer_name}</b></span>}
                  {r.match_status === "matched" ? (
                    <span data-testid={`join-match-${r.id}`} className="rounded-lg bg-green-100 text-green-800 border border-green-200 px-2 py-1 font-semibold">✓ Matched in People</span>
                  ) : (
                    <span data-testid={`join-review-${r.id}`} className="rounded-lg bg-amber-100 text-amber-800 border border-amber-200 px-2 py-1 font-semibold">Needs director review</span>
                  )}
                </div>
                {r.match_note && <p className="mt-2 text-xs text-gray-600 bg-gray-50 rounded-lg p-2">{r.match_note}</p>}
                {(r.matched_parent_name || r.matched_dancer_name) && (
                  <p className="mt-1.5 text-xs text-gray-700">
                    {r.matched_parent_name && <>Parent on file: <b>{r.matched_parent_name}</b></>}
                    {r.matched_dancer_name && <> · Dancer on file: <b>{r.matched_dancer_name}</b></>}
                  </p>
                )}
                {r.message && <p className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">{r.message}</p>}
                <p className="mt-2 text-[11px] text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</p>

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {STATUSES.map((s) => (
                    <button key={s.key} data-testid={`join-set-${s.key}-${r.id}`} onClick={() => setStatus(r.id, s.key)} disabled={r.status === s.key}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${r.status === s.key ? "opacity-40 cursor-default" : "hover:border-gray-400"}`}>
                      {s.label}
                    </button>
                  ))}
                  <button data-testid={`join-delete-${r.id}`} onClick={() => remove(r.id)} className="ml-auto text-gray-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-2.5 pt-2.5 border-t">
                  {r.converted_parent_id ? (
                    <span data-testid={`join-converted-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700"><CheckCircle2 className="w-4 h-4" />Added to People</span>
                  ) : r.match_status === "matched" ? (
                    <p className="text-xs text-muted-foreground">This family is already in People. Grant Hub access from their record in People (no new account is created here).</p>
                  ) : (
                    <div className="space-y-1.5">
                      <button data-testid={`join-convert-${r.id}`} onClick={() => convert(r)}
                        className="inline-flex items-center gap-2 bg-black text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-gray-800 transition-colors">
                        <UserPlus className="w-4 h-4 text-teal" />Add to People manually
                      </button>
                      <p className="text-[11px] text-muted-foreground">Only if you've confirmed this is a genuinely new record. Registration is normally handled in the Parent Portal.</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
